import { useMaxWindowDimensions } from '@ui/theme/useRWD'
import { colors } from '@ui/colors'
import { LinearGradient } from 'expo-linear-gradient'
import { useEffect } from 'react'
import { View, StyleSheet } from 'react-native'
import Animated, {
  Easing,
  measure,
  MeasuredDimensions,
  useAnimatedRef,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated'
import cn from '@ui/utils/cn'

const Outline = (props: {
  size: number
  offset?: number
  borderRadius: number
  className?: string
}) => {
  const { size, borderRadius, className } = props
  const offset = (props.offset ?? size) * 2
  return (
    <View
      style={{
        borderRadius: borderRadius + offset,
        borderWidth: size,
        top: -offset,
        left: -offset,
        right: -offset,
        bottom: -offset,
      }}
      className={cn('absolute border-black bg-white', className)}
    />
  )
}

const AnimatedGradient = Animated.createAnimatedComponent(LinearGradient)

interface Props {
  size: number
  offset?: number
  borderRadius: number
  className?: string
  color?: string
}

export const OutlineAnimated = (props: Props) => {
  const { size, className, color = colors.black } = props
  const offset = (props.offset ?? size) * 2
  const borderRadius = props.borderRadius + offset

  const animatedRef = useAnimatedRef<View>()
  const animation = useSharedValue(0)

  const screen = useMaxWindowDimensions()

  useEffect(() => {
    animation.set(
      withRepeat(withTiming(1, { duration: 3000, easing: Easing.linear }), -1)
    )
  }, [])

  const boxDimensions = useSharedValue<MeasuredDimensions | null>(null)

  const animatedStyle = useAnimatedStyle(() => {
    const value = animation.value % 1

    let box = boxDimensions.value
    if (!box) {
      const measured = measure(animatedRef)
      if (measured) {
        box = measured
      }
      boxDimensions.set(box)
    }

    const multiplier = 1.5

    const width = box?.width || screen.width
    const height = box?.height || screen.height

    const boxSize = Math.max(width, height) * multiplier
    return {
      width: boxSize,
      height: boxSize,
      top: height / 2 - boxSize / 2,
      left: -boxSize * 0.1,
      transform: [
        { rotate: `${value * 360}deg` },
        {
          translateY: 0,
        },
      ],
    }
  })

  return (
    <Animated.View
      ref={animatedRef}
      onLayout={() => {
        boxDimensions.set(measure(animatedRef))
      }}
      style={{
        borderRadius,
        top: -offset,
        left: -offset,
        right: -offset,
        bottom: -offset,
        overflow: 'hidden',
      }}
      className={cn('absolute', className)}
    >
      <Animated.View
        className="flex items-center justify-center"
        style={[
          {
            position: 'absolute',
          },
          animatedStyle,
        ]}
      >
        <AnimatedGradient
          colors={[
            colors.white0,
            colors.white0,
            color,
            color,
            colors.white0,
            colors.white0,
          ]}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
      </Animated.View>
      <View
        style={{
          borderRadius: borderRadius - size,
          margin: size,
        }}
        className="flex-1 bg-white"
      />
    </Animated.View>
  )
}

export default Outline
